import * as React from 'react';

import {FormContext, useFormInstance} from './context';
import {toFormApi} from './formApi';
import type {FormApi, FormValues} from './types';

const EMPTY_VALUES: FormValues = {};

type Props<TValues extends FormValues> = {
  /**
   * Keep `''` as a value when a field is cleared, instead of leaving it out of
   * the submitted values.
   */
  allowEmptyStrings?: boolean;
  children: React.ReactNode;
  /** Receives an imperative handle on the form, for resetting it and so on. */
  formApiRef?: React.RefObject<FormApi | null>;
  /**
   * Seeds the fields. Fields pick these up as they mount, and the form
   * re-seeds from them if they change while it is still untouched — so a form
   * rendered before its data arrives fills in once it does.
   *
   * This doubles as the form's declared shape: `onSubmit` receives values typed
   * from whatever is passed here, so reading a field that doesn't exist — or
   * whose name is spelled wrong — is a compile error instead of an `undefined`
   * that reaches the server.
   */
  initialValues?: TValues;
  /**
   * Runs once every field has validated. Returning a promise keeps the form in
   * its submitting state until it settles.
   *
   * `values` is typed from `initialValues`. That's an approximation worth
   * knowing about, in both directions: fields address the form by string name,
   * so a field can introduce a key `initialValues` never mentioned, and a field
   * that unmounts without `keepState` (or whose value the user cleared) leaves
   * its key out of the submitted values entirely. The type describes the form
   * as authored, not everything the store can hold.
   */
  onSubmit?: (formState: {values: NoInfer<TValues>}) => unknown;
} & Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'>;

/**
 * Wraps its children in a `<form>` and gives them a form to attach to. Fields
 * inside reach it with `useField`; anything else can use `useFieldState`,
 * `useFieldApi`, `useFormState`, or `useFormApi`.
 *
 * Submitting validates every field first, and `onSubmit` runs only if they all
 * pass.
 *
 * The value type is inferred from `initialValues` and nowhere else — an
 * annotation on `onSubmit`'s parameter can't widen it back to `any`. A form
 * with no `initialValues` falls back to values keyed by string, as before.
 */
const Form = <TValues extends FormValues = FormValues>({
  allowEmptyStrings = false,
  children,
  formApiRef,
  initialValues,
  onSubmit,
  ...rest
}: Props<TValues>) => {
  // `onSubmit` is usually a fresh closure every render; the form is handed a
  // stable one that calls whichever is current.
  const onSubmitRef = React.useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const form = useFormInstance(initialValues ?? EMPTY_VALUES, values =>
    // The store is keyed by string name and isn't generic, so this is the one
    // place the untyped store meets the typed contract above.
    onSubmitRef.current?.({values: values as TValues}),
  );

  const context = React.useMemo(
    () => ({allowEmptyStrings, form}),
    [allowEmptyStrings, form],
  );

  React.useImperativeHandle(formApiRef, () => toFormApi(form), [form]);

  return (
    <FormContext.Provider value={context}>
      <form
        {...rest}
        onSubmit={event => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        {children}
      </form>
    </FormContext.Provider>
  );
};

export default Form;
